import { FormEvent, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Activity,
  ArrowLeft,
  Dumbbell,
  Heart,
  Plus,
  Trash2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLogWorkout } from '../hooks/mutations/useLogWorkout';
import { dayjs } from '../lib/utils';
import {
  WorkflowError,
  workflowActionsClass,
  workflowFieldClass,
  workflowPrimaryClass,
  workflowSecondaryClass,
  workflowSurfaceClass,
} from '../components/WorkflowForm';

export type Exercise = {
  movement: string;
  sets: { reps: number; weight: number }[];
};
export type WorkoutData = {
  type: 'run' | 'strength' | 'recovery' | 'other';
  distance?: number;
  duration: number;
  notes?: string;
  date: string;
  exercises?: Exercise[];
};

const workoutTypeOptions = [
  { value: 'run', label: 'Run', icon: Activity },
  { value: 'strength', label: 'Strength', icon: Dumbbell },
  { value: 'recovery', label: 'Recovery', icon: Heart },
  { value: 'other', label: 'Other', icon: Activity },
] as const;

const durationOptions = Array.from(
  { length: 24 },
  (_, index) => (index + 1) * 15,
);
export default function LogWorkout() {
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const [workoutData, setWorkoutData] = useState<WorkoutData>({
    type: 'run',
    duration: 45,
    distance: undefined,
    notes: '',
    date: dayjs().format('YYYY-MM-DD'),
    exercises: [],
  });

  const isStrength = workoutData.type === 'strength';
  const showDistance =
    workoutData.type === 'run' || workoutData.type === 'other';

  const logWorkout = useLogWorkout({
    onError: (error) => {
      setFormError(error.message || 'Failed to log workout');
    },
  });

  const updateExercise = (exerciseIndex: number, updatedExercise: Exercise) => {
    setWorkoutData({
      ...workoutData,
      exercises:
        workoutData.exercises?.map((exercise, index) =>
          index === exerciseIndex ? updatedExercise : exercise,
        ) || [],
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (workoutData.duration <= 0) {
      setFormError('Please select a valid duration.');
      return;
    }
    if (
      isStrength &&
      (!workoutData.exercises || workoutData.exercises.length === 0)
    ) {
      setFormError('Add at least one exercise to record a strength session.');
      return;
    }

    logWorkout.mutate(workoutData);
  };

  return (
    <div className="mx-auto max-w-2xl px-1 pb-24 md:px-0">
      <Button
        variant="ghost"
        className="mb-4 -ml-3 hidden gap-2 md:flex"
        onClick={() => navigate({ to: '/workouts' })}
      >
        {formError ? <WorkflowError>{formError}</WorkflowError> : null}
        <ArrowLeft className="h-4 w-4" />
        Back to Workouts
      </Button>
      <form
        onSubmit={handleSubmit}
        className={`${workflowSurfaceClass} space-y-6`}
      >
          <div>
            <h1 className="text-2xl font-black text-brand-midnight">
              Log workout
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Record the work you completed today so it stays with your training history.
            </p>
        </div>

        <div>
          <label className="mb-3 block text-sm font-semibold text-gray-700">
            Workout type
          </label>
          <p className="-mt-1 mb-3 text-xs text-gray-500">
            Choose the session type that best describes the work you did.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {workoutTypeOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = workoutData.type === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    setWorkoutData({
                      ...workoutData,
                      type: option.value,
                      exercises:
                        option.value === 'strength'
                          ? workoutData.exercises
                          : [],
                    })
                  }
                  className={`rounded-xl border-2 p-4 text-center ${isSelected ? 'border-brand-volt bg-brand-blueSoft' : 'border-gray-200'}`}
                >
                  <Icon className="mx-auto mb-2 h-5 w-5" />
                  <span className="text-sm font-bold">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <label className="block text-sm font-semibold text-gray-700">
          Workout date
          <input
            type="date"
            max={dayjs().format('YYYY-MM-DD')}
            required
             className={workflowFieldClass}
            value={workoutData.date}
            onChange={(event) =>
              setWorkoutData({ ...workoutData, date: event.target.value })
            }
          />
        </label>

        <label className="block text-sm font-semibold text-gray-700">
          Time
          <select
             className={workflowFieldClass}
            value={workoutData.duration}
            onChange={(event) =>
              setWorkoutData({
                ...workoutData,
                duration: Number(event.target.value),
              })
            }
          >
            {durationOptions.map((minutes) => (
              <option key={minutes} value={minutes}>
                {minutes} minutes
              </option>
            ))}
          </select>
        </label>

        {showDistance ? (
          <label className="block text-sm font-semibold text-gray-700">
            Distance (miles)
            <input
              type="number"
              step="0.1"
              min="0"
               className={workflowFieldClass}
              value={workoutData.distance || ''}
              onChange={(event) =>
                setWorkoutData({
                  ...workoutData,
                  distance: event.target.value
                    ? Number(event.target.value)
                    : undefined,
                })
              }
              placeholder="0.0"
            />
          </label>
        ) : null}

        {isStrength ? (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">
                Exercises
              </label>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-sm font-bold text-brand-cobalt"
                onClick={() =>
                  setWorkoutData({
                    ...workoutData,
                    exercises: [
                      ...(workoutData.exercises || []),
                      { movement: '', sets: [{ reps: 0, weight: 0 }] },
                    ],
                  })
                }
              >
                <Plus className="h-4 w-4" />
                Add exercise
              </button>
            </div>
            <div className="space-y-3">
              {workoutData.exercises?.map((exercise, exerciseIndex) => (
                <div
                  key={exerciseIndex}
                  className="rounded-xl border border-gray-200 p-3"
                >
                  <div className="flex gap-2">
                    <label className="sr-only" htmlFor={`movement-${exerciseIndex}`}>Exercise name</label>
                    <input
                      id={`movement-${exerciseIndex}`}
                       className={workflowFieldClass}
                      value={exercise.movement}
                      onChange={(event) =>
                        updateExercise(exerciseIndex, {
                          ...exercise,
                          movement: event.target.value,
                        })
                      }
                      placeholder="Exercise name"
                    />
                    <button
                      type="button"
                      className="mt-1 text-red-600"
                      aria-label={`Remove exercise ${exerciseIndex + 1}`}
                      onClick={() =>
                        setWorkoutData({
                          ...workoutData,
                          exercises:
                            workoutData.exercises?.filter(
                              (_, index) => index !== exerciseIndex,
                            ) || [],
                        })
                      }
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="mt-3 space-y-2">
                    {exercise.sets.map((set, setIndex) => (
                      <div key={setIndex} className="grid grid-cols-2 gap-2">
                        <label className="sr-only" htmlFor={`reps-${exerciseIndex}-${setIndex}`}>Reps</label>
                        <input
                          id={`reps-${exerciseIndex}-${setIndex}`}
                          type="number"
                          min="1"
                           className={workflowFieldClass}
                          value={set.reps || ''}
                          onChange={(event) =>
                            updateExercise(exerciseIndex, {
                              ...exercise,
                              sets: exercise.sets.map((item, index) =>
                                index === setIndex
                                  ? {
                                      ...item,
                                      reps: Number(event.target.value),
                                    }
                                  : item,
                              ),
                            })
                          }
                          placeholder="Reps"
                        />
                        <label className="sr-only" htmlFor={`weight-${exerciseIndex}-${setIndex}`}>Weight</label>
                        <input
                          id={`weight-${exerciseIndex}-${setIndex}`}
                          type="number"
                          min="0"
                          step="0.25"
                           className={workflowFieldClass}
                          value={set.weight || ''}
                          onChange={(event) =>
                            updateExercise(exerciseIndex, {
                              ...exercise,
                              sets: exercise.sets.map((item, index) =>
                                index === setIndex
                                  ? {
                                      ...item,
                                      weight: Number(event.target.value),
                                    }
                                  : item,
                              ),
                            })
                          }
                          placeholder="Weight"
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      className="text-sm font-bold text-brand-cobalt"
                      onClick={() =>
                        updateExercise(exerciseIndex, {
                          ...exercise,
                          sets: [...exercise.sets, { reps: 0, weight: 0 }],
                        })
                      }
                    >
                      + Add set
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <label className="block text-sm font-semibold text-gray-700">
          Notes
          <textarea
            rows={4}
             className={workflowFieldClass}
            value={workoutData.notes}
            onChange={(event) =>
              setWorkoutData({ ...workoutData, notes: event.target.value })
            }
            placeholder="How did it feel?"
          />
        </label>

        <div className={`${workflowActionsClass} -mx-5 -mb-5 bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] md:sticky md:bottom-0 dark:bg-slate-900`}>
          <Button
            type="button"
            variant="outline"
             className={workflowSecondaryClass}
            onClick={() => navigate({ to: '/workouts' })}
          >
            Cancel
          </Button>
          <Button
            type="submit"
             className={workflowPrimaryClass}
            disabled={logWorkout.isPending}
          >
            {logWorkout.isPending ? 'Saving...' : 'Add workout'}
          </Button>
        </div>
      </form>
    </div>
  );
}
